import { FaceMesh } from "@mediapipe/face_mesh";

// --- KMeans + helpers ---
function kmeans(pixels, k = 3, maxIters = 10) {
  if (pixels.length === 0) return [0, 0, 0];
  let centroids = [];
  for (let i = 0; i < k; i++) {
    centroids.push(pixels[Math.floor(Math.random() * pixels.length)]);
  }
  for (let iter = 0; iter < maxIters; iter++) {
    const clusters = Array.from({ length: k }, () => []);
    for (const p of pixels) {
      let best = 0;
      let bestDist = Infinity;
      centroids.forEach((c, idx) => {
        const d = (p[0]-c[0])**2 + (p[1]-c[1])**2 + (p[2]-c[2])**2;
        if (d < bestDist) { bestDist = d; best = idx; }
      });
      clusters[best].push(p);
    }
    centroids = clusters.map((cluster, idx) => {
      if (cluster.length === 0) return centroids[idx];
      const mean = cluster.reduce((a, b) => [a[0]+b[0], a[1]+b[1], a[2]+b[2]], [0,0,0]);
      return mean.map(v => v/cluster.length);
    });
  }
  return centroids[0].map(Math.round);
}

function rgbToHex([r, g, b]) {
  return `#${[r,g,b].map(x => x.toString(16).padStart(2, "0")).join("")}`;
}

async function getLandmarks(faceMesh, image) {
  return new Promise((resolve) => {
    faceMesh.onResults((res) => {
      if (res.multiFaceLandmarks && res.multiFaceLandmarks.length > 0) {
        resolve(res.multiFaceLandmarks[0]);
      } else {
        resolve(null);
      }
    });
    faceMesh.send({ image });
  });
}

function getRegionColor(image, landmarks, indices) {
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0);

  ctx.beginPath();
  ctx.moveTo(landmarks[indices[0]].x * image.width, landmarks[indices[0]].y * image.height);
  for (let i = 1; i < indices.length; i++) {
    ctx.lineTo(landmarks[indices[i]].x * image.width, landmarks[indices[i]].y * image.height);
  }
  ctx.closePath();
  ctx.clip();

  const imgData = ctx.getImageData(0, 0, image.width, image.height).data;
  const pixels = [];
  for (let i = 0; i < imgData.length; i += 4) {
    if (imgData[i+3] > 0) pixels.push([imgData[i], imgData[i+1], imgData[i+2]]);
  }
  if (pixels.length === 0) return "#000000";
  return rgbToHex(kmeans(pixels, 3));
}

function getMostCommonColor(colors) {
  if (!colors) {
    return "#000000"
  }

  // Simple approach: return the first color
  // In a more sophisticated implementation, you could:
  // 1. Convert to RGB and average
  // 2. Use color distance metrics
  // 3. Apply more advanced color theory

  let color = colors[0]
  return color
  
}

function combineAnalysisResults(results) {

  try {
    let skin_colors = []
    let eye_colors = []
    let lip_colors = []
    let hair_colors = []

    for (const result of results) {
      skin_colors.push(result.colors.skin_tone)
      eye_colors.push(result.colors.eye_color)
      lip_colors.push(result.colors.lip_color)
      hair_colors.push(result.colors.hair_color)
    }

    const combined = {
      skin_tone: getMostCommonColor(skin_colors),
      eye_color: getMostCommonColor(eye_colors),
      lip_color: getMostCommonColor(lip_colors),
      hair_color: getMostCommonColor(hair_colors)
    }

    return combined

  } catch (err) {
    return {"error": err}
  }
}

async function analyzeFaces(base64Images) {
  const faceMesh = new FaceMesh({
    locateFile: (file) =>
      `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
  });

  faceMesh.setOptions({
    staticImageMode: true,
    maxNumFaces: 1,
    refineLandmarks: true,
    minDetectionConfidence: 0.5,
  });

  const results = [];

  for (const img64 of base64Images) {
    const img = new Image();
    img.src = img64;
    await new Promise((res) => (img.onload = res));

    const landmarks = await getLandmarks(faceMesh, img);
    if (!landmarks) {
      results.push({ face_detected: false });
      continue;
    }

    const skin_tone = getRegionColor(img, landmarks, [10, 151, 234, 127, 162]);
    const eye_color  = getRegionColor(img, landmarks, [33, 133, 362, 263]);
    const lip_color  = getRegionColor(img, landmarks, [61, 146, 91, 181]);
    const hair_color = getRegionColor(img, landmarks, [10, 151, 9, 234, 127]);

    results.push({
      face_detected: true,
      colors: {
        skin_tone, eye_color, lip_color, hair_color
      }
    });
  }

  const combined = combineAnalysisResults(results)

  return {
    'success': true,
    'results': combined,
    'images_analyzed': results.length,
    'total_images': base64Images.length
}

}

export default analyzeFaces;